import { useRef, useState } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { CompanyResource, CompanyResourceCategory, RESOURCE_CATEGORIES } from '../../types/companyResourceTypes';
import { inferFileType, isTrainingCategory, mapFileTypeToTopicStyle } from '../../utils/companyResourceUtils';
import {
    createCompanyResource,
    updateCompanyResource,
} from '../../graphql/companyResourceQueries';
import { createTrainingTopic, updateTrainingTopic } from '../../graphql/trainingQueries';
import { useUserContext } from '../../contexts/UserContext';
import { Btn, BtnIconOnly, IconClose, InputField, Select } from '../ui';

interface ResourceModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => Promise<void>;
    editResource: CompanyResource | null;
    companyId: string;
    existingFolders?: string[];
}

export const ResourceModal = ({ isOpen, onClose, onSave, editResource, companyId, existingFolders = [] }: ResourceModalProps) => {
    const { companyOwner, companyUser } = useUserContext();
    const [title, setTitle] = useState(editResource?.title || '');
    const [description, setDescription] = useState(editResource?.description || '');
    const [category, setCategory] = useState<string>(editResource?.category || '');
    const [folderName, setFolderName] = useState(editResource?.folderName || '');
    const [newFolderName, setNewFolderName] = useState('');
    const [folderMode, setFolderMode] = useState<'existing' | 'new'>(editResource?.folderName ? 'existing' : 'existing');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [saving, setSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    if (!isOpen) return null;

    const currentUser = companyOwner || companyUser;
    const userId = currentUser?.id || '';
    const userName = currentUser?.name || '';

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!title.trim()) errs.title = 'Title is required';
        if (!category) errs.category = 'Category is required';
        if (!editResource && !selectedFile) errs.file = 'Please select a file to upload';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            // Auto-fill title from filename if empty
            if (!title.trim()) {
                const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                setTitle(nameWithoutExt);
            }
        }
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            if (editResource) {
                // Update metadata only (no re-upload)
                const effectiveFolder = folderMode === 'new' ? newFolderName.trim() : folderName.trim();
                await updateCompanyResource({
                    id: editResource.id,
                    title: title.trim(),
                    description: description.trim() || null,
                    category,
                    folderName: effectiveFolder || null,
                });

                // Sync linked TrainingTopic if one exists
                if (editResource.trainingTopicId) {
                    try {
                        await updateTrainingTopic({
                            id: editResource.trainingTopicId,
                            topicId: title.trim(),
                            description: description.trim() || null,
                        } as any);
                    } catch (syncErr) {
                        console.error('Failed to sync linked TrainingTopic:', syncErr);
                    }
                }

                await onSave();
                return;
            }

            // New resource — upload file
            const file = selectedFile!;
            const fileType = inferFileType(file.name);
            const fileKey = `public/companyResources/${companyId}/${uuidv4()}-${file.name}`;

            await uploadData({
                path: fileKey,
                data: file,
                options: {
                    onProgress: ({ transferredBytes, totalBytes }) => {
                        if (totalBytes) setUploadProgress(Math.round((transferredBytes / totalBytes) * 100));
                    },
                },
            }).result;

            const effectiveFolder = folderMode === 'new' ? newFolderName.trim() : folderName.trim();

            // Create CompanyResource record
            const newResource = await createCompanyResource({
                companyId,
                title: title.trim(),
                description: description.trim() || null,
                category,
                fileType,
                contentFilePath: fileKey,
                folderName: effectiveFolder || null,
                uploadedByUserId: userId,
                uploadedByUserName: userName,
            });

            // Auto-create TrainingTopic if training-eligible category
            if (isTrainingCategory(category as CompanyResourceCategory)) {
                const topicId = uuidv4();
                const style = mapFileTypeToTopicStyle(fileType);
                const newTopic = await createTrainingTopic({
                    companyId,
                    topicId,
                    style,
                    description: title.trim(),
                    timeValue: 0,
                    contentFilePath: fileKey,
                });
                // Link bidirectionally
                if (!newResource || !newTopic) throw new Error('Failed to create company resource or training topic');
                await updateCompanyResource({ id: newResource.id, trainingTopicId: newTopic.id });
                await updateTrainingTopic({ id: newTopic.id, companyResourceId: newResource.id } as any);
            }

            await onSave();
        } catch (err) {
            console.error('Error saving resource:', err);
            setErrors({ _form: 'Failed to save resource. Please try again.' });
        } finally {
            setSaving(false);
            setUploadProgress(0);
        }
    };

    return (
        <div className="cr-modal-overlay">
            <div className="cr-modal cr-modal--wide cr-modal--company-resource">
                <div className="cr-modal-header">
                    <h2 className="cr-modal-title">{editResource ? 'Edit Resource' : 'Add Company Resource'}</h2>
                    <BtnIconOnly size="sm" icon={<IconClose />} ariaLabel="Close" onClick={onClose} />
                </div>
                <div className="cr-modal-body">
                    {errors._form && <div className="cr-error-banner">⚠️ {errors._form}</div>}

                    <div className="cr-field">
                        <InputField
                            label="Title *"
                            inputId="company-resource-title"
                            value={title}
                            onChange={setTitle}
                            hasError={!!errors.title}
                            ariaDescribedBy={errors.title ? 'company-resource-title-error' : undefined}
                        />
                        {errors.title && <div id="company-resource-title-error" className="cr-field-error-text">{errors.title}</div>}
                    </div>

                    <div className="cr-field">
                        <label className="cr-field-label">Description</label>
                        <textarea className="cr-field-textarea" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional description..." />
                    </div>

                    <div className="cr-field">
                        <label className="cr-field-label" htmlFor="company-resource-folder">Folder</label>
                        <Select
                            inputId="company-resource-folder"
                            ariaLabel="Folder"
                            value={folderMode === 'new' ? '' : folderName}
                            onChange={(value) => {
                                setFolderMode('existing');
                                setFolderName(value);
                                setNewFolderName('');
                            }}
                            placeholder="No folder (unfiled)"
                            options={[
                                { value: '', label: 'No folder (unfiled)' },
                                ...existingFolders.map((folder) => ({ value: folder, label: folder })),
                            ]}
                            actionOption={{
                                label: '+ New Folder',
                                onClick: () => {
                                    setFolderMode('new');
                                    setFolderName('');
                                },
                            }}
                        />
                        {folderMode === 'new' && (
                            <InputField
                                className="cr-new-folder-field"
                                ariaLabel="New folder name"
                                placeholder="Enter new folder name"
                                value={newFolderName}
                                onChange={setNewFolderName}
                                autoFocus
                            />
                        )}
                    </div>

                    <div className="cr-field">
                        <label className="cr-field-label" htmlFor="company-resource-category">
                            Category <span className="cr-required">*</span>
                        </label>
                        <Select
                            inputId="company-resource-category"
                            ariaLabel="Category"
                            value={category}
                            onChange={setCategory}
                            placeholder="Select category"
                            hasError={!!errors.category}
                            options={RESOURCE_CATEGORIES.map((item) => ({ value: item.name, label: item.name }))}
                        />
                        {errors.category && <div className="cr-field-error-text">{errors.category}</div>}
                        {category && isTrainingCategory(category as CompanyResourceCategory) && !editResource && (
                            <div className="cr-info-banner" style={{ marginTop: 8 }}>
                                <span className="cr-info-banner-icon">ℹ️</span>
                                This category will automatically create a Training Topic linked to this resource.
                            </div>
                        )}
                    </div>

                    {!editResource && (
                        <div className="cr-field">
                            <label className="cr-field-label">File <span className="cr-required">*</span></label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                style={{ display: 'none' }}
                                accept=".pdf,.mp4,.mov,.jpg,.jpeg,.png,.xlsx,.xls,.docx,.doc"
                                onChange={handleFileSelect}
                            />
                            {!selectedFile ? (
                                <div
                                    className={`cr-upload-zone${errors.file ? ' cr-field-error' : ''}`}
                                    onClick={() => fileInputRef.current?.click()}
                                >
                                    <div className="cr-upload-zone-icon">📁</div>
                                    <div className="cr-upload-zone-text">
                                        Click to select a file<br />
                                        <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>PDF, Video, Image, Excel, or Document</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="cr-upload-file-info">
                                    <span>📎</span>
                                    <span className="cr-upload-file-name">{selectedFile.name}</span>
                                    <span style={{ color: '#6b7280', fontSize: '0.78rem' }}>
                                        ({(selectedFile.size / 1024 / 1024).toFixed(1)} MB)
                                    </span>
                                    <BtnIconOnly
                                        size="sm"
                                        variant="danger"
                                        icon={<IconClose />}
                                        ariaLabel="Remove file"
                                        onClick={() => setSelectedFile(null)}
                                    />
                                </div>
                            )}
                            {errors.file && <div className="cr-field-error-text">{errors.file}</div>}
                        </div>
                    )}

                    {saving && uploadProgress > 0 && uploadProgress < 100 && (
                        <div style={{ marginTop: 8, fontSize: '0.82rem', color: '#6b7280' }}>
                            Uploading... {uploadProgress}%
                        </div>
                    )}
                </div>
                <div className="cr-modal-footer">
                    <Btn variant="secondary-outlined" buttonText="Cancel" buttonOnClick={onClose} buttonDisabled={saving} />
                    <Btn
                        variant="primary"
                        buttonText={saving ? 'Saving...' : (editResource ? 'Update' : 'Upload & Save')}
                        buttonOnClick={handleSubmit}
                        buttonDisabled={saving}
                    />
                </div>
            </div>
        </div>
    );
};
